import React, { useState, useEffect } from 'react';
import {
  Box, Avatar, Typography, IconButton,
  Container, styled,
  TextField, Dialog, DialogContent, Button, InputAdornment, DialogActions
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  Add as AddIcon, Lock,
  Visibility, VisibilityOff
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import AlertService from "../../components/Alerts/alerts";
import AuthService from "../Authentication/auth.service";
import AddressService from './addressService';
import styledEmotion from '@emotion/styled';
import AddAddressForm from './AddAddressPopup';
import UpdateAddressPopup from './UpdateAddressPopup';
import axios from "axios";
import PasswordResetPopup from './PasswordResetPopup';
import EditProfile from './EditProfile';


const PageHeading = styled(Typography)({
  fontSize: '28px',
  color: '#3488A4',
  marginBottom: '32px',
  fontWeight: '600',
  textAlign: 'center'
});

const ProfileHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '48px',
});

const UserAvatar = styled(Avatar)({
  width: '80px',
  height: '80px',
  fontSize: '32px',
  backgroundColor: '#184363',
  borderRadius: '12px'
});

const UserInfo = styled(Box)({
  '& .MuiTypography-h5': {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '4px'
  },
  '& .MuiTypography-body1': {
    color: '#666',
    fontSize: '13px'
  }
});

const SectionHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  '& .MuiTypography-h6': {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  }
});

const ActionLink = styled(Button)({
  color: '#3488A4',
  fontSize: '14px',
  textTransform: 'none',
  padding: '4px 0',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline'
  }
});

const FormField = styled(Box)({
  marginBottom: '24px',
  '& .label': {
    fontSize: '14px',
    color: '#666',
    marginBottom: '8px'
  },
  '& .value': {
    fontSize: '13px',
    color: '#333',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  }
});

const AddressList = styled(Box)({
  '& .address-item': {
    padding: '16px',
    borderRadius: '8px',
    backgroundColor: '#f8f9fa',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& .label': {
      fontSize: '13px',
      fontWeight: '600',
      marginBottom: '4px'
    },
    '& .address': {
      fontSize: '14px',
      color: '#666'
    }
  }
});




const OrangeAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: '#184363',
  borderRadius: '8px',
  width: 50,
  height: 50,
  fontSize: 20,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 'bold',
  marginBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const SectionBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: '8px',
  boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
  backgroundColor: 'white',
  width: '100%',  // Increased width
  margin: '0 auto',  // Center the box
}));


const CircularIcon = styled(Box)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  color: '#3488A4',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f0f0f0',
  marginRight: theme.spacing(2),
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  border: '1px solid #e0e0e0',
  color: '#3488A4',
  marginLeft: theme.spacing(1),
}));

const LoaderOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
`;


const StyledPasswordDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    width: '90%',
    maxWidth: '500px',
    margin: 0,
    padding: theme.spacing(4),
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
}));

const Logo = styled('img')({
  width: '120px',
  height: '120px',
});

const StyledPasswordTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#F0F5FB',
    '& fieldset': {
      border: '1px solid #e0e0e0',
    },
    '&:hover fieldset': {
      border: '1px solid #3488A4',
    },
    '&.Mui-focused fieldset': {
      border: '2px solid #3488A4',
    },
    '&.Mui-error fieldset': {
      border: '2px solid #d32f2f',
      backgroundColor: '#ffebee',
    },
    '&.Mui-error:hover fieldset': {
      border: '2px solid #d32f2f',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '13px',
    color: '#757575',
    '&.Mui-focused': {
      color: '#3488A4',
    },
    '&.Mui-error': {
      color: '#d32f2f',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
    fontSize: '13px',
  },
  marginBottom: theme.spacing(1),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#3488A4',
  color: 'white',
  borderRadius: '12px',
  padding: '14px',
  fontSize: '18px',
  fontWeight: 'bold',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#d35400',
  },
}));

const MyProfile = ({ onClose, onProfileUpdate }) => {
  const [userData, setUserData] = useState(JSON.parse(localStorage.getItem("user")));
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, addressId: null, address: '' });
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [isUpdateAddressOpen, setIsUpdateAddressOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [logo, setLogo] = useState(localStorage.getItem("system_logo"));
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [touched, setTouched] = useState({ newPassword: false, confirmPassword: false });

  // Prevent background scrolling when popup is open
  useEffect(() => {
    if (deleteConfirmDialog.open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [deleteConfirmDialog.open]);

  // Update the handleEditProfile function
  const handleEditProfile = () => {
    setShowEditProfile(true);
  };

  // Add function to handle profile updates
  const handleProfileUpdate = (updatedUser) => {
    setUserData(updatedUser);
    // Also update localStorage and notify parent component
    localStorage.setItem("user", JSON.stringify(updatedUser));
    if (onProfileUpdate) {
      onProfileUpdate();
    }
  };

  // Password validation functions
  const validateOldPassword = (password) => {
    if (!password || password.trim().length === 0) {
      return t('Old password is required.');
    }
    return null;
  };

  const validateNewPassword = (password) => {
    if (!password || password.trim().length === 0) {
      return t('New password is required.');
    }
    if (password.length < 6) {
      return t('Password must be at least 6 characters long.');
    }
    if (password.length > 50) {
      return t('Password must be less than 50 characters.');
    }
    return null;
  };

  const validateConfirmPassword = (confirmPassword, newPassword) => {
    if (!confirmPassword || confirmPassword.trim().length === 0) {
      return t('Please confirm your new password.');
    }
    if (confirmPassword !== newPassword) {
      return t('Passwords do not match.');
    }
    return null;
  };

  const validatePasswordField = (fieldName, value, newPassword = '') => {
    let error = null;

    switch (fieldName) {
      case 'oldPassword':
        error = validateOldPassword(value);
        break;
      case 'newPassword':
        error = validateNewPassword(value);
        break;
      case 'confirmPassword':
        error = validateConfirmPassword(value, newPassword);
        break;
      default:
        break;
    }

    setPasswordErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return error;
  };


  const handleAddAddress = () => {
    setOpenAddressDialog(true);
  };

  const handleUpdateAddress = (address) => {
    setSelectedAddress(address);
    setIsUpdateAddressOpen(true);
  };

  // const handleAddAddress = () => {
  //   navigate('/add_address');
  // };

  const handleAddressAdded = () => {
    // Refresh the addresses list
    fetchAddresses();
  };


  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!userData) {
      AuthService.logout();
      navigate("/login");
    } else {
      fetchAddresses();
    }
  }, []);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const response = await AddressService.getAddresses(userData._id);
      if (response.data.status) {
        setAddresses(response.data.data);
      } else {
        AlertService.swalErrorAlert(response.data.message || t("Error fetching addresses."));
      }
    } catch (error) {
      AlertService.swalErrorAlert(t("Error fetching addresses. Please try again."));
    } finally {
      setLoading(false);
    }
  };


  const handlePasswordChange = async () => {
    // Validate all fields and update error states
    const oldPasswordError = validateOldPassword(passwordData.oldPassword);
    const newPasswordError = validateNewPassword(passwordData.newPassword);
    const confirmPasswordError = validateConfirmPassword(passwordData.confirmPassword, passwordData.newPassword);

    // Update all error states
    setPasswordErrors({
      oldPassword: oldPasswordError,
      newPassword: newPasswordError,
      confirmPassword: confirmPasswordError
    });

    if (oldPasswordError || newPasswordError || confirmPasswordError) {
      AlertService.swalErrorAlert(t("Please fix the validation errors before proceeding."));
      return;
    }

    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BASEURL;
      const payload = {
        functionName: 'UpdateCustomerPassword',
        postData: {
          _id: userData._id,
          old_password: passwordData.oldPassword,
          new_password: passwordData.newPassword,
        },
      };
      const headers = {
        'Content-Type': 'application/json',
        'token': localStorage.getItem("session-id")
      };
      const response = await axios.post(API_URL, payload, { headers });

      if (response.data.status) {
        AlertService.swalSuccessAlert(t("Password changed successfully."));
        setOpenPasswordDialog(false);
        setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
      } else {
        AlertService.swalErrorAlert(response.data.message || t("Error changing password."));
      }
    } catch (error) {
      console.error("Error changing password:", error);
      AlertService.swalErrorAlert(t("Error changing password. Please try again."));
    } finally {
      setLoading(false);
    }
  };


  const handleEditAddress = (address) => {
    navigate(`/update_address`, { state: { address } });
  };

  const handleDeleteConfirm = (addressId, address) => {
    setDeleteConfirmDialog({ open: true, addressId, address });
  };

  const handleDeleteAddress = async () => {
    const { addressId } = deleteConfirmDialog;
    setDeleteConfirmDialog({ open: false, addressId: null, address: '' });

    try {
      const response = await AddressService.deleteAddress(addressId);
      if (response.data.status) {
        AlertService.swalSuccessAlert(t("Address deleted successfully."));
        fetchAddresses();
      } else {
        AlertService.swalErrorAlert(response.data.message || t("Error deleting address."));
      }
    } catch (error) {
      AlertService.swalErrorAlert(t("Error deleting address. Please try again."));
    }
  };


  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <PageHeading>Profile</PageHeading>

      <ProfileHeader>
        <UserAvatar>{userData.name.charAt(0).toUpperCase()}</UserAvatar>
        <UserInfo>
          <Typography variant="h5">{userData.name}</Typography>
          <Typography variant="body1">{userData.email}</Typography>
        </UserInfo>
      </ProfileHeader>

      <Box sx={{ mb: 3 }}>
        <SectionHeader>
          <Typography variant="h6">Personal Info</Typography>
          <ActionLink onClick={handleEditProfile}>Edit</ActionLink>
        </SectionHeader>

        <FormField>
          <Typography className="label">Full Name</Typography>
          <Box className="value">{userData.name}</Box>
        </FormField>

        <FormField>
          <Typography className="label">Email</Typography>
          <Box className="value">{userData.email}</Box>
        </FormField>

        <FormField>
          <Typography className="label">Phone Number</Typography>
          <Box className="value">{userData.phone}</Box>
        </FormField>

        <FormField>
          <Typography className="label">MMC Number</Typography>
          <Box className="value">{userData.mmc_number || 'Not provided'}</Box>
        </FormField>

        <FormField>
          <Typography className="label">APC Number</Typography>
          <Box className="value">{userData.apc_number || 'Not provided'}</Box>
        </FormField>
      </Box>

      <Box>
        <SectionHeader>
          <Typography variant="h6">My Addresses</Typography>
          <ActionLink startIcon={<AddIcon />} onClick={handleAddAddress}>
            Add Address
          </ActionLink>
        </SectionHeader>

        <AddressList>
          {addresses.map((address) => (
            <Box key={address._id} className="address-item">
              <Box>
                <Typography className="address">
                  {`${address.unit}, ${address.block}, ${address.location}`}
                </Typography>
              </Box>
              <Box>
                <IconButton onClick={() => handleUpdateAddress(address)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={() => handleDeleteConfirm(address._id, `${address.unit}, ${address.block}, ${address.location}`)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </AddressList>
      </Box>

      <Box sx={{ mt: 4, borderTop: '1px solid #e0e0e0', pt: 4 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Lock />}
          onClick={() => setOpenPasswordDialog(true)}
          sx={{
            color: '#3488A4',
            borderColor: '#3488A4',
            '&:hover': {
              borderColor: '#3488A4',
              backgroundColor: 'rgba(52, 136, 164, 0.04)'
            }
          }}
        >
          Change Password
        </Button>
      </Box>
      {/* <PasswordResetPopup open={openPasswordDialog} handleClose={() => setOpenPasswordDialog(false)} /> */}

      <StyledPasswordDialog open={openPasswordDialog} onClose={() => {
        setOpenPasswordDialog(false);
        setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
      }}>
        <DialogContent>
          <LogoContainer>
            <Logo src={logo} alt="Logo" />
          </LogoContainer>

          <Typography variant="h5" align="center" sx={{ mb: 3, fontWeight: 'bold', color: '#333' }}>
            {t('Change Password')}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '13px', mb: 1 }}>
              {t('Old Password')}
            </Typography>
            <StyledPasswordTextField
              fullWidth
              name="oldPassword"
              label={t("Enter Old Password")}
              type={showOldPassword ? "text" : "password"}
              value={passwordData.oldPassword}
              onChange={(e) => {
                const value = e.target.value;
                setPasswordData({ ...passwordData, oldPassword: value });
                validatePasswordField('oldPassword', value);
              }}
              error={!!passwordErrors.oldPassword}
              helperText={passwordErrors.oldPassword}
              sx={{
                '& .MuiFormHelperText-root': {
                  color: passwordErrors.oldPassword ? '#d32f2f' : '#757575',
                  fontSize: '12px',
                  marginTop: '4px',
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                      sx={{ color: '#666' }}
                    >
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '13px', mb: 1 }}>
              {t('New Password')}
            </Typography>
            <StyledPasswordTextField
              fullWidth
              name="newPassword"
              label={t("Enter New Password")}
              type={showNewPassword ? "text" : "password"}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              onFocus={() => setPasswordErrors(prev => ({ ...prev, newPassword: '' }))}
              onBlur={() => {
                setTouched(prev => ({ ...prev, newPassword: true }));

                const newPassError = validatePasswordField('newPassword', passwordData.newPassword);
                setPasswordErrors(prev => ({ ...prev, newPassword: newPassError }));

                if (passwordData.confirmPassword) {
                  const confirmError = validatePasswordField(
                    'confirmPassword',
                    passwordData.confirmPassword,
                    passwordData.newPassword
                  );
                  setPasswordErrors(prev => ({ ...prev, confirmPassword: confirmError }));
                }
              }}
              error={touched.newPassword && !!passwordErrors.newPassword}
              helperText={touched.newPassword ? passwordErrors.newPassword : ''}
              sx={{
                '& .MuiFormHelperText-root': {
                  color: passwordErrors.newPassword ? '#d32f2f' : '#757575',
                  fontSize: '12px',
                  marginTop: '4px',
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                      sx={{ color: '#666' }}
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '13px', mb: 1 }}>
              {t('Confirm Password')}
            </Typography>
            <StyledPasswordTextField
              fullWidth
              name="confirmPassword"
              label={t("Confirm New Password")}
              type={showConfirmPassword ? "text" : "password"}
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              onFocus={() => setPasswordErrors(prev => ({ ...prev, confirmPassword: '' }))}
              onBlur={() => {
                const confirmError = validatePasswordField(
                  'confirmPassword',
                  passwordData.confirmPassword,
                  passwordData.newPassword
                );
                setPasswordErrors(prev => ({ ...prev, confirmPassword: confirmError }));

                if (passwordData.newPassword) {
                  const newPassError = validatePasswordField('newPassword', passwordData.newPassword);
                  setPasswordErrors(prev => ({ ...prev, newPassword: newPassError }));
                }
              }}
              error={!!passwordErrors.confirmPassword}
              helperText={passwordErrors.confirmPassword}
              sx={{
                '& .MuiFormHelperText-root': {
                  color: passwordErrors.confirmPassword ? '#d32f2f' : '#757575',
                  fontSize: '12px',
                  marginTop: '4px',
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: '#666' }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setOpenPasswordDialog(false);
                setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
                setPasswordErrors({});
              }}
              sx={{
                borderRadius: '8px',
                padding: '12px',
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '14px',
                borderColor: '#3488A4',
                color: '#3488A4',
                '&:hover': {
                  borderColor: '#3488A4',
                  backgroundColor: 'rgba(52, 136, 164, 0.04)'
                }
              }}
            >
              {t("Cancel")}
            </Button>
            <SubmitButton
              fullWidth
              onClick={handlePasswordChange}
              disabled={loading || Object.values(passwordErrors).some(error => error !== null)}
              sx={{
                borderRadius: '8px',
                padding: '12px',
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '14px',
                opacity: Object.values(passwordErrors).some(error => error !== null) ? 0.6 : 1,
              }}
            >
              {loading ? t("Updating...") : t("Update Password")}
            </SubmitButton>
          </Box>
        </DialogContent>
      </StyledPasswordDialog>


      <EditProfile
        open={showEditProfile}
        onClose={() => setShowEditProfile(false)}
        userData={userData}
        onUpdate={handleProfileUpdate}
      />

      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, addressId: null, address: '' })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            textAlign: 'center',
            padding: '20px'
          }
        }}
      >
        <DialogContent sx={{ padding: 0 }}>
          <Box sx={{
            backgroundColor: '#d32f2f',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '0 auto 20px',
            color: 'white',
            fontSize: '30px'
          }}>
            ?
          </Box>
          <Typography variant="h6" sx={{
            fontSize: '18px',
            color: '#333',
            marginBottom: '20px'
          }}>
            {t("Remove address: {{address}}?", { address: deleteConfirmDialog.address })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: 0, display: 'flex' }}>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, addressId: null, address: '' })}
            sx={{
              flex: 1,
              padding: '15px',
              backgroundColor: '#3488A4',
              color: 'white',
              borderRadius: 0,
              '&:hover': { backgroundColor: '#2a6d84' }
            }}
          >
            No
          </Button>
          <Button
            onClick={handleDeleteAddress}
            sx={{
              flex: 1,
              padding: '15px',
              backgroundColor: '#c0392b',
              color: 'white',
              borderRadius: 0,
              '&:hover': { backgroundColor: '#a93226' }
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <AddAddressForm
        key={addresses?.length}
        open={openAddressDialog}
        onClose={() => setOpenAddressDialog(false)}
        addresses={addresses}
        // onSelectAddress={(address) => {
        //   handleSelectAddress(address._id);
        //   handleCloseAddressDialog();
        // }}
        onAddNew={handleAddressAdded}
      />
      <UpdateAddressPopup
        open={isUpdateAddressOpen}
        onClose={() => setIsUpdateAddressOpen(false)}
        address={selectedAddress}
        onUpdateAddress={(updatedAddress) => {
          fetchAddresses();
        }}
      />
    </Container >
  );
};

export default MyProfile;